import React, { useEffect, useState, useCallback, useContext, createContext } from "react";
import { WagmiProvider, useAccount, useBalance, useDisconnect, useChainId, useSwitchChain, useSendTransaction, useWaitForTransactionReceipt } from "wagmi";
import { parseEther } from "viem";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, Wallet, Trophy, Gamepad2, Sprout, LogOut, RefreshCw, X, Zap, Rocket, Dice5, Coins, ExternalLink, Calendar, Menu, GraduationCap, Users, LayoutDashboard, User, Coffee, Trees } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import "@/App.css";
import { wagmiConfig, cronosMainnet, BURN_ADDRESS, TREASURY_ADDRESS } from "@/wagmi";
import { registerReferral, isSafeOwner, connectUser, getUser, getConfig, getGlobalStats, getLeaderboard, verifyBurn, plantCrop, harvestCrop, spinWheel, blackjackAction, crashAction, claimStreak } from "@/api";
import { BurnAnyCoin } from "@/components/burn/BurnAnyCoin";
import { ConnectWalletButton } from "@/components/wallet/ConnectWalletButton";
import { MobileTabBar, TAB_PATHS } from "@/components/nav/MobileTabBar";

// ====================== App Context ======================
const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [config, setConfig] = useState(null);
  const [globalStats, setGlobalStats] = useState(null);
  const [toast, setToast] = useState(null);
  const [sound, setSound] = useState(true);

  const showToast = useCallback((msg, kind = "info") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  }, []);

  useEffect(() => { getConfig().then(setConfig).catch(() => {}); }, []);
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref && /^0x[0-9a-fA-F]{40}$/.test(ref)) localStorage.setItem("flame.ref", ref.toLowerCase());
  }, []);
  useEffect(() => {
    const id = setInterval(() => getGlobalStats().then(setGlobalStats).catch(() => {}), 15000);
    getGlobalStats().then(setGlobalStats).catch(() => {});
    return () => clearInterval(id);
  }, []);

  return (
    <AppCtx.Provider value={{ user, setUser, config, globalStats, showToast, sound, setSound }}>
      {children}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            className={`fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] md:bottom-6 left-1/2 w-[calc(100%-2rem)] max-w-md md:w-auto -translate-x-1/2 z-50 card-tile ${toast.kind === "error" ? "border-red-500/60" : "card-flame"}`}
            data-testid="toast">
            <div className="text-sm font-semibold">{toast.msg}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppCtx.Provider>
  );
}

// ====================== Wallet Hook ======================
export function useWalletUser() {
  const { address, isConnected } = useAccount();
  const { setUser, user, showToast } = useApp();

  useEffect(() => {
    if (!isConnected || !address) { setUser(null); return; }
    connectUser(address).then((u) => {
      setUser(u);
      const ref = localStorage.getItem("flame.ref");
      if (ref && ref !== address.toLowerCase()) registerReferral(address, ref).catch(() => {}).finally(() => localStorage.removeItem("flame.ref"));
    }).catch((e) => showToast(e?.response?.data?.detail || "Failed to load profile", "error"));
    const id = setInterval(() => {
      if (address) getUser(address).then(setUser).catch(() => {});
    }, 5000);
    return () => clearInterval(id);
  }, [isConnected, address, setUser, showToast]);

  const refresh = useCallback(() => { if (address) return getUser(address).then(setUser); }, [address, setUser]);
  return { address, isConnected, user, refresh };
}

// ====================== Navbar ======================
function Navbar() {
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { globalStats, user } = useApp();
  const location = useLocation();

  const [isOwner, setIsOwner] = useState(false);
  useEffect(() => { if (address) isSafeOwner(address).then(setIsOwner).catch(() => setIsOwner(false)); else setIsOwner(false); }, [address]);
  const wrongNetwork = isConnected && chainId !== cronosMainnet.id;
  const short = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "");

  const links = [
    { to: "/dashboard", label: "Dashboard", icon: Zap, tid: "nav-link-dashboard" },
    { to: "/farm", label: "Farm", icon: Sprout, tid: "nav-link-farm", exact: true },
    { to: "/farm/valley", label: "Valley", icon: Sprout, tid: "nav-link-valley" },
    { to: "/arcade", label: "Arcade", icon: Gamepad2, tid: "nav-link-arcade" },
    { to: "/leaderboard", label: "Leaderboard", icon: Trophy, tid: "nav-link-leaderboard" },
    { to: "/hubs", label: "Social Hubs", icon: Users, tid: "nav-link-hubs" },
    { to: "/buy", label: "Buy", icon: Coins, tid: "nav-link-buy" },
    { to: "/token", label: "Token", icon: Flame, tid: "nav-link-token" },
    { to: "/university", label: "AI University", icon: GraduationCap, tid: "nav-link-university" },
    { to: "/roast", label: "ROAST", icon: Coffee, tid: "nav-link-roast" },
    { to: "/nwood", label: "NWOOD", icon: Trees, tid: "nav-link-nwood" },
    ...(isOwner ? [{ to: "/operator", label: "Operator", icon: Zap, tid: "nav-link-operator" }] : []),
  ];

  return (
    <>
    <nav data-testid="navbar" className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#0F101A]/85 border-b border-[#2A2E45]/80">
      <div className="max-w-[1720px] mx-auto px-4 h-16 flex items-center gap-2 sm:gap-4">
        <Link to="/" className="flex items-center gap-2 font-display text-xl font-bold" data-testid="nav-logo">
          <img src="/brand/flame-64.png" alt="FLAME" className="w-8 h-8" />
          <span>FLAME</span>
          
        </Link>
        <div className="flex items-center gap-0.5 ml-4">
          {links.map((l) => {
            const active = l.exact ? location.pathname === l.to : location.pathname.startsWith(l.to);
            return (
              <Link key={l.to} to={l.to} data-testid={l.tid}
                className={`hidden xl:flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition ${active ? "bg-orange-500/15 text-orange-300" : "text-slate-300 hover:bg-white/5"}`}>
                <l.icon size={16} /> {l.label}
              </Link>
            );
          })}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-menu-button" className="hidden md:flex xl:hidden btn-tactile btn-ghost p-2 items-center gap-1 text-sm" aria-label="Open menu">
                <Menu size={18} /> <span className="hidden sm:inline">Menu</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-[#181A26] border-[#2A2E45] text-slate-100 w-52" data-testid="nav-menu">
              {links.map((l) => (
                <DropdownMenuItem key={l.to} asChild className="cursor-pointer focus:bg-white/5 focus:text-orange-300">
                  <Link to={l.to} data-testid={`${l.tid}-mobile`} className="flex items-center gap-2"><l.icon size={16} /> {l.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex-1" />

        {globalStats && (
          <div className="hidden lg:block"><div className="flex items-center chip border-red-500/40 bg-red-500/10" data-testid="nav-mainnet-chip">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="font-pixel text-[10px] text-red-200">MAINNET</span>
          </div></div>
        )}
        {globalStats && (
          <div className="hidden min-[1800px]:block"><div className="flex items-center chip" data-testid="nav-cro-burn-counter">
            <Flame size={14} className="text-orange-500" />
            <span className="font-pixel text-[10px] whitespace-nowrap">{Number(globalStats.total_cro_burned).toFixed(2)} CRO BURNED</span>
          </div></div>
        )}

        {user && (
          <div className="hidden md:flex items-center gap-2">
            <div className="chip" data-testid="nav-credits"><Coins size={14} className="text-cyan-400" /> {user.credits}</div>
            <div className="chip" data-testid="nav-flame"><Flame size={14} className="text-orange-500" /> {user.flame}</div>
          </div>
        )}

        {!isConnected ? (
          <div className="flex items-center gap-2">
            <ConnectWalletButton tid="nav-connect-wallet-button" className="btn-tactile btn-flame font-display whitespace-nowrap text-sm sm:text-base px-4 sm:px-5">
              <Wallet size={16} className="inline mr-2" />Connect<span className="hidden sm:inline"> Wallet</span>
            </ConnectWalletButton>
          </div>
        ) : wrongNetwork ? (
          <button data-testid="nav-switch-network" onClick={() => switchChain({ chainId: cronosMainnet.id })}
            className="btn-tactile btn-gold font-display text-sm whitespace-nowrap">Switch<span className="hidden sm:inline"> to Cronos Mainnet</span></button>
        ) : (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="chip hover:brightness-125 whitespace-nowrap" data-testid="nav-profile-chip">{short(address)}</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#181A26] border-[#2A2E45] text-slate-100 w-52" data-testid="nav-profile-menu">
                <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/5 focus:text-orange-300">
                  <Link to="/profile" data-testid="nav-profile-link" className="flex items-center gap-2"><User size={16} /> Profile</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/5 focus:text-orange-300">
                  <Link to="/creator" data-testid="nav-creator-dashboard-link" className="flex items-center gap-2"><LayoutDashboard size={16} /> Creator Dashboard</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button data-testid="nav-disconnect" onClick={() => disconnect()} className="hidden md:inline-flex btn-tactile btn-ghost p-2"><LogOut size={14} /></button>
          </div>
        )}
      </div>
    </nav>
    <MobileTabBar moreLinks={links.filter((l) => !TAB_PATHS.includes(l.to))} />
    </>
  );
}

// ====================== Landing ======================
function Landing() {
  const { isConnected } = useAccount();
  const navigate = useNavigate();
  const { globalStats } = useApp();

  useEffect(() => { if (isConnected) navigate("/dashboard"); }, [isConnected, navigate]);

  return (
    <div className="pt-24 relative">
      <div className="hero-glow absolute inset-0 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 pt-10 pb-20 relative">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
          <div className="chip mb-6 border-red-500/50 bg-red-500/10"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="font-pixel text-[10px] text-red-200">CRONOS MAINNET • CHAIN 25 • REAL VALUE</span></div>
          <h1 data-testid="hero-headline" className="font-display text-4xl sm:text-5xl md:text-7xl font-black leading-[1.05] drop-shadow-[0_4px_12px_rgba(255,85,0,0.5)]">
            Burn <span className="text-orange-500">CRO</span>.<br/>
            Earn <span className="text-amber-400">FLAME</span>.<br/>
            Dominate the Cronos Arcade.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl">A play-to-earn arcade for degens who love fire. Burn CRO to the dead wallet, grow a cozy FLAME farm, and spin, deal &amp; crash your way to the top of the leaderboard.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <ConnectWalletButton tid="hero-cta-play-now" className="btn-tactile btn-flame font-display text-lg px-7 py-4 w-full sm:w-auto">
              <Wallet className="inline mr-2" size={18} /> Connect Wallet &amp; Play
            </ConnectWalletButton>
            <Link to="/whitepaper" className="btn-tactile btn-ghost font-display text-lg px-7 py-4 text-center" data-testid="hero-whitepaper-link">Read the White Paper</Link>
          </div>

          {globalStats && (
            <div className="mt-10 grid grid-cols-3 gap-2 sm:gap-4 w-full max-w-xl">
              <Stat label="CRO Burned" value={Number(globalStats.total_cro_burned).toFixed(4)} color="text-orange-500" tid="landing-stat-burned" />
              <Stat label="FLAME Minted" value={globalStats.total_flame_minted} color="text-amber-400" tid="landing-stat-flame" />
              <Stat label="Players" value={globalStats.total_players} color="text-cyan-400" tid="landing-stat-players" />
            </div>
          )}

          <TreasuryMeter />
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-4 gap-5">
          <FeatureCard icon={Sprout} color="text-emerald-400" title="FLAME Farm" desc="Plant 4 crop tiers. Watch 'em grow. Harvest FLAME on cozy cooldowns." />
          <FeatureCard icon={Dice5} color="text-amber-400" title="Lucky Spin" desc="Spin for up to 50x FLAME. Jackpot explodes in flame particles." />
          <FeatureCard icon={Gamepad2} color="text-rose-400" title="Cronos Blackjack" desc="Beat the dealer. 2.5x on natural 21." />
          <FeatureCard icon={Rocket} color="text-cyan-400" title="Rocket Crash" desc="Launch, climb, cash out before the burn explosion." />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color, tid }) {
  return (
    <div className="card-tile text-center py-4 px-2" data-testid={tid}>
      <div className={`font-display text-lg sm:text-2xl font-black break-all ${color}`}>{value}</div>
      <div className="font-pixel text-[9px] text-slate-400 mt-1">{label}</div>
    </div>
  );
}

function TreasuryMeter() {
  const [m, setM] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/treasury/meter`)
      .then((r) => r.json()).then((j) => !cancelled && setM(j)).catch(() => {});
    load();
    const id = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  if (!m) return null;
  const flameSupplyBurnedPct = (m.flame_total_supply && Number(m.flame_total_supply) > 0)
    ? (100 - (Number(m.flame_total_supply) / 10_000_000_000) * 100) : 0;
  return (
    <div className="mt-6 w-full max-w-xl card-tile card-flame" data-testid="treasury-meter">
      <div className="flex items-center justify-between">
        <div className="font-pixel text-[9px] text-slate-400">PROJECT TREASURY</div>
        <a className="text-xs text-cyan-400 underline" target="_blank" rel="noreferrer" href={`https://cronoscan.com/address/${m.treasury}`}>{m.treasury.slice(0, 10)}…</a>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 sm:gap-3 text-center [&_.font-display]:text-base sm:[&_.font-display]:text-xl [&_.font-display]:break-all">
        <div><div className="font-display text-xl font-black text-sky-400">{m.cro_balance ? Number(m.cro_balance).toFixed(4) : "—"}</div><div className="text-[9px] font-pixel text-slate-500">CRO</div></div>
        <div><div className="font-display text-xl font-black text-orange-400">{m.flame_balance ? Number(m.flame_balance).toLocaleString(undefined, {maximumFractionDigits: 0}) : "—"}</div><div className="text-[9px] font-pixel text-slate-500">FLAME HELD</div></div>
        <div><div className="font-display text-xl font-black text-rose-400">{flameSupplyBurnedPct.toFixed(4)}%</div><div className="text-[9px] font-pixel text-slate-500">BURNED</div></div>
      </div>
    </div>
  );
}
function FeatureCard({ icon: Icon, color, title, desc }) {
  return (
    <div className="card-tile card-flame relative overflow-hidden">
      <Icon size={28} className={`${color} mb-2`} />
      <div className="font-display text-xl font-bold">{title}</div>
      <div className="text-sm text-slate-400 mt-1">{desc}</div>
    </div>
  );
}

// ====================== Dashboard ======================
function Dashboard() {
  const { address, isConnected, user, refresh } = useWalletUser();
  const { globalStats, config, showToast } = useApp();
  const { data: bal } = useBalance({ address, chainId: cronosMainnet.id, query: { enabled: !!address } });
  const [burnAmount, setBurnAmount] = useState("0.1");
  const [verifying, setVerifying] = useState(false);
  const { data: hash, sendTransaction, isPending, error } = useSendTransaction();
  const receipt = useWaitForTransactionReceipt({ hash });

  const doBurn = () => {
    if (!address) return;
    const n = Number(burnAmount);
    if (!n || n <= 0) return showToast("Enter a positive CRO amount", "error");
    const target = config?.burn_splitter;
    if (!target) {
      showToast("CRO burn is pending audit & mainnet deploy", "error");
      return;
    }
    sendTransaction({ to: target, value: parseEther(String(n)), chainId: cronosMainnet.id });
  };

  useEffect(() => {
    if (receipt.isSuccess && hash && !verifying) {
      setVerifying(true);
      verifyBurn(address, hash)
        .then((res) => { showToast(`🔥 Burned ${res.cro_burned} CRO → +${res.credits_awarded} credits`); refresh(); })
        .catch((e) => showToast(e?.response?.data?.detail || "Verification failed", "error"))
        .finally(() => setVerifying(false));
    }
  }, [receipt.isSuccess]);  // eslint-disable-line

  if (!isConnected) return <ConnectPrompt />;

  return (
    <div className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="font-display text-3xl sm:text-4xl font-black">Dashboard</h1>
        <p className="text-slate-400 mt-1">Welcome back, degen 🔥</p>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <StatCard tid="stat-cro-balance" icon={Wallet} color="text-sky-400" label="CRO Balance" value={bal ? Number(bal.formatted).toFixed(4) : "—"} />
          <StatCard tid="stat-flame-earned" icon={Flame} color="text-orange-500" label="FLAME Earned" value={user?.flame ?? 0} />
          <StatCard tid="stat-credits" icon={Coins} color="text-cyan-400" label="Game Credits" value={user?.credits ?? 0} />
          <StatCard tid="stat-user-rank" icon={Trophy} color="text-yellow-400" label="Daily Streak" value={`${user?.streak ?? 0} 🔥`} />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card-tile card-flame md:col-span-2">
            <h2 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2"><Flame className="text-orange-500 flame-flicker shrink-0" /> Burn CRO → Earn Game Credits</h2>
            <p className="text-sm text-slate-400 mt-1">1 CRO = 100 credits. Credits power all games. 99% of your CRO is burned to <code>0x…dEaD</code> on Cronos Mainnet and 1% supports the project treasury via the on-chain splitter contract. This is a REAL on-chain tx and <b>irreversible</b>.</p>
            <div className="mt-4 flex gap-2" data-testid="burn-quick-amounts">
              {["1", "5", "10", "50"].map((v) => (
                <button key={v} onClick={() => setBurnAmount(v)} data-testid={`burn-quick-${v}`}
                  className={`chip flex-1 sm:flex-none justify-center py-2 ${burnAmount === v ? "border-orange-500/60 text-orange-300" : ""}`}>{v} CRO</button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <input data-testid="burn-amount-input" type="number" inputMode="decimal" min="0.001" step="0.01" value={burnAmount} onChange={(e) => setBurnAmount(e.target.value)}
                className="bg-[#12131C] border border-[#2A2E45] rounded-xl px-4 py-3 w-full sm:w-40 font-mono" />
              <button data-testid="burn-cro-button" onClick={doBurn} disabled={isPending || verifying || receipt.isLoading || !config?.burn_enabled} className="btn-tactile btn-flame w-full sm:w-auto">
                {!config?.burn_enabled ? "Pending audit" : isPending ? "Sign in wallet…" : receipt.isLoading ? "Confirming…" : verifying ? "Verifying…" : <>Burn &amp; Mint Credits <Flame className="inline ml-1" size={16} /></>}
              </button>
              {hash && (
                <a className="text-xs text-cyan-400 underline break-all" href={`https://explorer.cronos.org/tx/${hash}`} target="_blank" rel="noreferrer">View tx ↗</a>
              )}
            </div>
            {error && <div className="mt-3 text-rose-400 text-sm">{error.shortMessage || error.message}</div>}
          </div>

          <StreakCard />
        </div>

        <BurnAnyCoin onCredited={refresh} />

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/farm" className="card-tile flex items-center gap-4 hover:border-emerald-400/40 transition" data-testid="dashboard-go-farm">
            <Sprout size={36} className="text-emerald-400" />
            <div>
              <div className="font-display text-xl font-bold">Enter the Farm 🌱</div>
              <div className="text-sm text-slate-400">Plant crops, harvest FLAME on cozy timers.</div>
            </div>
          </Link>
          <Link to="/arcade" className="card-tile flex items-center gap-4 hover:border-orange-500/40 transition" data-testid="dashboard-go-arcade">
            <Gamepad2 size={36} className="text-orange-500" />
            <div>
              <div className="font-display text-xl font-bold">Enter the Arcade 🎰</div>
              <div className="text-sm text-slate-400">Spin, deal, and launch rockets.</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, tid }) {
  return (
    <div className="card-tile" data-testid={tid}>
      <div className="flex items-center justify-between">
        <div className="font-pixel text-[9px] text-slate-400">{label}</div>
        <Icon size={18} className={color} />
      </div>
      <div className={`mt-2 font-display text-2xl sm:text-3xl font-black break-all ${color}`}>{value}</div>
    </div>
  );
}

function StreakCard() {
  const { address, user, refresh } = useWalletUser();
  const { showToast } = useApp();
  const today = new Date().toISOString().slice(0, 10);
  const alreadyClaimed = user?.last_streak_claim === today;
  const claim = async () => {
    try {
      const r = await claimStreak(address);
      showToast(`+${r.flame_bonus} FLAME • Day ${r.streak} streak!`);
      refresh();
    } catch (e) { showToast(e?.response?.data?.detail || "Can’t claim", "error"); }
  };
  return (
    <div className="card-tile" data-testid="daily-streak-modal">
      <div className="flex items-center gap-2"><Calendar className="text-amber-400" /><h3 className="font-display text-xl font-bold">Daily Streak</h3></div>
      <p className="text-sm text-slate-400 mt-1">Login every day for compounding FLAME bonuses.</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="font-display text-4xl font-black text-amber-400">{user?.streak ?? 0}🔥</div>
        <button data-testid="claim-daily-streak-button" disabled={alreadyClaimed} onClick={claim} className="btn-tactile btn-gold">{alreadyClaimed ? "Claimed today" : "Claim +FLAME"}</button>
      </div>
    </div>
  );
}

export function ConnectPrompt() {
  return (
    <div className="pt-32 px-4 flex flex-col items-center text-center">
      <Flame className="text-orange-500 flame-flicker" size={48} />
      <h2 className="font-display text-3xl font-bold mt-3">Connect your wallet to play</h2>
      <p className="text-slate-400 mt-2">MetaMask or Crypto.com DeFi Wallet • Cronos Mainnet</p>
      <ConnectWalletButton tid="connect-prompt-button" className="btn-tactile btn-flame mt-6 font-display w-full max-w-xs sm:w-auto">Connect Wallet</ConnectWalletButton>
    </div>
  );
}

export default function AppWrap() {
  // dynamic import of QueryClient to avoid re-registering; use the one from index.js is fine
  return (
    <WagmiProvider config={wagmiConfig}>
      <AppProvider>
        <BrowserRouter>
            <ComplianceGateWrapper>
              <Navbar />
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/farm" element={<FarmPage />} />
                <Route path="/farm/valley" element={<FlameValleyPage />} />
                <Route path="/arcade" element={<ArcadePage />} />
                <Route path="/arcade/spin" element={<SpinPage />} />
                <Route path="/arcade/blackjack" element={<BlackjackPage />} />
                <Route path="/arcade/crash" element={<CrashPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/whitepaper" element={<WhitepaperPage />} />
                <Route path="/token" element={<TokenPage />} />
                <Route path="/university" element={<UniversityPage />} />
                <Route path="/hubs" element={<HubsPage />} />
                <Route path="/roast" element={<RoastPage />} />
                <Route path="/nwood" element={<NwoodPage />} />
                <Route path="/hubs/new" element={<HubStudioPage />} />
                <Route path="/hubs/:slug/edit" element={<HubStudioPage />} />
                <Route path="/hubs/:slug" element={<HubPage />} />
                <Route path="/creator" element={<CreatorDashboardPage />} />
                <Route path="/buy" element={<BuyPage />} />
                <Route path="/operator" element={<OperatorPage />} />
              </Routes>
              <Footer />
            </ComplianceGateWrapper>
        </BrowserRouter>
      </AppProvider>
    </WagmiProvider>
  );
}

// Lazy imports to keep this file lean
import FarmPage from "@/pages/Farm";
import FlameValleyPage from "@/pages/FlameValley";
import ArcadePage from "@/pages/Arcade";
import SpinPage from "@/pages/Spin";
import BlackjackPage from "@/pages/Blackjack";
import CrashPage from "@/pages/Crash";
import LeaderboardPage from "@/pages/Leaderboard";
import ProfilePage from "@/pages/Profile";
import TermsPage from "@/pages/Terms";
import PrivacyPage from "@/pages/Privacy";
import WhitepaperPage from "@/pages/Whitepaper";
import TokenPage from "@/pages/Token";
import UniversityPage from "@/pages/University";
import HubsPage from "@/pages/Hubs";
import RoastPage from "@/pages/Roast";
import NwoodPage from "@/pages/Nwood";
import HubStudioPage from "@/pages/HubStudio";
import HubPage from "@/pages/HubPage";
import CreatorDashboardPage from "@/pages/CreatorDashboard";
import BuyPage from "@/pages/Buy";
import OperatorPage from "@/pages/Operator";
import ComplianceGate from "@/components/ComplianceGate";
import Footer from "@/components/Footer";

function ComplianceGateWrapper({ children }) {
  const { config } = useApp();
  const blocked = config?.geo_block_countries || [];
  return <ComplianceGate blockedCountries={blocked}>{children}</ComplianceGate>;
}
